import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDate } from '@/hooks/useDate';
import { useInfoByStatus } from '@/hooks/useInfoByStatus';
import { useTimeAgo } from '@/hooks/useTimeAgo';
import {
  useAppointmentDetailsSlideStore,
  useAppointmentsStore,
} from '@/stores/appointments';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import {
  ENotificationAppointmentsType,
  ERouterNames,
  EUserRoles,
  TNotificationAppointmentsCamel,
  TNotificationCamel,
  TUserSummaryCamel,
} from '@/types';
import {
  formatSessionDateWithEnd,
  getTimeUntilStart,
} from '@/utils/convertDate';
import { nameFormat } from '@/utils/valuesFormat';
import { SYSTEM_ID } from '@/utils/constants';

type TNotificationInfo = {
  description: string;
  button: {
    text: string;
    onClick: () => void;
  };
};

type TProps = {
  notification: TNotificationAppointmentsCamel;
  onClose: () => void;
};

export const useNotificationInfo = ({ notification, onClose }: TProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setToast } = useToastStore();
  const appointment = useAppointmentsStore((state) =>
    state.getAppointmentById(notification.meta?.appointmentId),
  );
  const { setOpen, setSelectedAppointment } = useAppointmentDetailsSlideStore();
  const { setTime, timeAgo } = useTimeAgo();

  const { checkRole, checkRoleIds } = useUsersStore((state) => ({
    checkRole: state.checkRole,
    checkRoleIds: state.checkRoleIds,
  }));

  const { displayDateFormat, displayTime } = useDate(
    notification.meta.start ?? '',
  );
  // Use only notifications.status, as it is not dynamic and will never change.
  const { rescheduledDate } = useInfoByStatus(notification.meta.status);

  const { displayDate: notificationStart } = useDate(notification?.meta?.start);
  const { displayDate: notificationEnd } = useDate(notification?.meta?.end);

  const { displayDate: notificationRescheduledStart } = useDate(
    rescheduledDate?.start ?? '',
  );

  const userByLastStatus = notification?.meta?.status?.at(-1)?.user;

  const formatNameWithFallback = (user?: Partial<TUserSummaryCamel>) => {
    return user ? nameFormat(user.firstName, user.lastName) : t('someone');
  };

  const userByLastStatusName = formatNameWithFallback(userByLastStatus);

  const defaultButtonText = t('dashboard.session-details');

  const handleDetailsClick = () => {
    onClose();
    if (!appointment) {
      setToast({
        title: t('error'),
        description: t('appointment-access-error'),
        variant: 'error',
      });
      return;
    }
    setSelectedAppointment(appointment);
    setOpen(true);
    navigate(ERouterNames.DASHBOARD);
  };

  const descriptionCancelCallback = (
    variant: 'expired' | 'cancel' = 'cancel',
  ) => {
    /**
     * Display the name of the user with whom the meeting is taking place, so we search for
     * the first user whose userInstitutionRoleId does not match ours.
     *
     * If the coordinator canceled, we show that the provider canceled.
     */
    const otherParticipant = appointment?.participants.find(
      ({ userInstitutionRoleId }) => !checkRoleIds([userInstitutionRoleId]),
    )?.user;

    const otherParticipantName = formatNameWithFallback(otherParticipant);
    return t(
      variant === 'cancel'
        ? 'notification-appointment-canceled'
        : 'notification-appointment-expired',
      {
        name: otherParticipantName,
        date: formatSessionDateWithEnd(notificationStart, notificationEnd),
      },
    );
  };

  const notificationFactory = (notification: TNotificationCamel) => {
    switch (notification.type) {
      case ENotificationAppointmentsType.APPOINTMENT_REQUESTED: {
        if (notification.userInstitutionRole.role === EUserRoles.COORDINATOR) {
          return {
            button: {
              text: t('dashboard.go-to-command-center'),
              onClick: () => {
                navigate(ERouterNames.COMMAND_CENTER);
                onClose();
              },
            },
            description:
              appointment?.scheduleType === 'scheduled'
                ? t('a-scheduled-type-request-has-been-scheduled')
                : t('an-on-demand-type-request-has-been-scheduled'),
          };
        }
        const descriptionCallback = () => {
          if (rescheduledDate) {
            return t('notification-appointment-rescheduled', {
              name: userByLastStatusName,
            });
          }
          const firstStatus = notification.meta.status
            ? notification.meta.status[0]
            : undefined;
          const userByFirstStatus = firstStatus ? firstStatus.user : undefined;

          // The provider always sees that the appointment was created by the patient, not the coordinator/provider.
          const requestorUser =
            (checkRole(EUserRoles.PROVIDER) &&
            [EUserRoles.COORDINATOR, EUserRoles.PROVIDER].some(
              (r) => r === userByFirstStatus?.role,
            )
              ? appointment?.participants[0].user
              : userByFirstStatus) ?? appointment?.createdBy;

          return t('notification-appointment-requested', {
            name: formatNameWithFallback(requestorUser),
            date: formatSessionDateWithEnd(notificationStart, notificationEnd),
          });
        };
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: descriptionCallback(),
        };
      }
      case ENotificationAppointmentsType.APPOINTMENT_UPDATED:
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description:
            notification?.meta?.status.at(-1)?.by === SYSTEM_ID
              ? t('notification-appointment-auto-confirmed')
              : t('notification-appointment-confirmed', {
                  name: userByLastStatusName,
                }),
        };
      case ENotificationAppointmentsType.APPOINTMENT_CANCELED:
      case ENotificationAppointmentsType.APPOINTMENT_EXPIRED: {
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: descriptionCancelCallback(
            notification.type ===
              ENotificationAppointmentsType.APPOINTMENT_EXPIRED
              ? 'expired'
              : 'cancel',
          ),
        };
      }
      case ENotificationAppointmentsType.APPOINTMENT_RESCHEDULE_DECLINED: {
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: t('notification-appointment-reschedule-decline', {
            dateTime: formatSessionDateWithEnd(
              notificationStart,
              notificationEnd,
            ),
          }),
        };
      }
      case ENotificationAppointmentsType.APPOINTMENT_REMINDER: {
        const descriptionCallback = () => {
          const timeUntilStart = getTimeUntilStart(
            notification.meta.start,
            notification.createdAt,
          );
          if (!timeUntilStart) return t('notification-error');
          const { timeValue, unitKey } = timeUntilStart;

          const timeUnit = t(unitKey, { count: timeValue });

          return t('common.notification-appointment-reminded', {
            name: formatNameWithFallback(
              appointment?.participants.find(
                ({ userInstitutionRoleId }) =>
                  !checkRoleIds([userInstitutionRoleId]),
              )?.user,
            ),
            count: timeValue,
            unit: timeUnit,
          });
        };
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: descriptionCallback(),
        };
      }
      case ENotificationAppointmentsType.APPOINTMENT_REASSIGNED: {
        const descriptionCallback = () => {
          const formerProviderName = formatNameWithFallback(
            notification.meta?.formerProvider,
          );
          const dateTime = formatSessionDateWithEnd(
            notificationStart,
            notificationEnd,
          );

          const patientParticipant = appointment?.participants?.find(
            (p) => p.user?.role === EUserRoles.PATIENT,
          );

          const newProviderParticipant = appointment?.participants?.find(
            (p) => p.user?.role === EUserRoles.PROVIDER,
          );

          const patientName = formatNameWithFallback(patientParticipant?.user);

          const newProviderName = formatNameWithFallback(
            newProviderParticipant?.user,
          );

          if (checkRole(EUserRoles.PROVIDER)) {
            return t('notification-appointment-reassigned-provider', {
              patientName,
              dateTime,
              newProviderName,
            });
          }

          return t('notification-appointment-reassigned-patient', {
            formerProviderName,
            dateTime,
            newProviderName,
          });
        };
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: descriptionCallback(),
        };
      }

      case ENotificationAppointmentsType.PAYMENT_REMINDER:
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: t('notification-payment-reminder', {
            date: displayDateFormat,
            time: displayTime,
          }),
        };
      case ENotificationAppointmentsType.WAITING_ROOM_JOINED:
        return {
          button: {
            text: defaultButtonText,
            onClick: handleDetailsClick,
          },
          description: t('notification-waiting-room-joined', {
            name: formatNameWithFallback(appointment?.participants[0].user),
            date: displayDateFormat,
            time: displayTime,
          }),
        };
      case ENotificationAppointmentsType.APPOINTMENT_FOLLOW_UP_REQUESTED:
        return {
          button: {
            text: t('dashboard.go-to-command-center'),
            onClick: () => {
              onClose();
              navigate(ERouterNames.COMMAND_CENTER, {
                state: {
                  appointmentId: notification.meta?.appointmentId ?? '',
                },
              });
            },
          },
          description: t('notification-follow-up-requested'),
        };
      default:
        return null;
    }
  };

  const notificationInfo: TNotificationInfo = notificationFactory(
    notification,
  ) ?? {
    description: t('notification-error'),
    button: { text: '', onClick: () => {} },
  };

  useEffect(() => {
    setTime(notification.createdAt);
  }, [notification.createdAt, setTime]);

  const isFormerProvider =
    notification.type ===
      ENotificationAppointmentsType.APPOINTMENT_REASSIGNED &&
    checkRoleIds([notification.meta?.formerProviderUserInstitutionRoleId]);

  return {
    isReadNotification: notification.readAt,
    isFormerProvider,
    notificationInfo,
    timeAgo,
    rescheduledStartDates: rescheduledDate
      ? {
          oldStart: notificationStart,
          newStart: notificationRescheduledStart,
        }
      : undefined,
    handleDetailsClick,
  };
};
